/**
 * UMD SDK 스크립트를 주입하고 `window[globalName]` 전역이 노출될 때까지 대기한다.
 *
 * 동작: 스크립트를 즉시 주입하고(SDK 는 평가되며 전역을 노출하므로 onload 시점에 전역을 잡아 resolve),
 * 이미 주입된 태그 재사용 등으로 onload 를 놓치는 경우를 대비해 `requestAnimationFrame` 폴링 가드를 둔다.
 * `timeoutMs` 는 "주입 후 SDK 가 끝내 안 나타날 때"의 실패 가드로만 동작한다.
 * (이전 구현은 폴링이 끝난 뒤에야 주입해 매 새로고침마다 고정 지연이 있었음 — 순서를 뒤집어 제거.)
 *
 * 클라이언트 전용(`.client.ts`) 플러그인에서만 호출되므로 `window`/`document` 접근이 안전하다.
 * (onboarding/survey 플러그인이 각각 두던 `waitForOnboardingSdk`/`waitForSurveySdk` 를 단일화.)
 */
export const waitForSdk = <T>(
    globalName: string,
    url: string,
    timeoutMs = 5000
): Promise<NonNullable<T>> => {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('window is not defined'));
    }
    const read = () => (window as unknown as Record<string, unknown>)[globalName] as T | undefined;

    const existingGlobal = read();
    if (existingGlobal) return Promise.resolve(existingGlobal as NonNullable<T>);

    return new Promise((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);
        if (!existing) {
            const script = document.createElement('script');
            script.src = url;
            script.async = true;
            script.onload = () => {
                const g = read();
                if (g) resolve(g as NonNullable<T>);
                else reject(new Error(`${globalName} not found after script load`));
            };
            script.onerror = () => reject(new Error(`Failed to load ${url}`));
            document.head.appendChild(script);
        }

        const start = Date.now();
        const poll = () => {
            const g = read();
            if (g) {
                resolve(g as NonNullable<T>);
                return;
            }
            if (Date.now() - start > timeoutMs) {
                reject(new Error(`${globalName} not available within timeout`));
                return;
            }
            requestAnimationFrame(poll);
        };
        requestAnimationFrame(poll);
    });
};
